import { useState } from 'react';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import likeEmoji from '../assets/like-emoji.png';
import loveEmoji from '../assets/love-emoji.png';
import careEmoji from '../assets/care-emoji.png';
import hahaEmoji from '../assets/hilarious-emoji.png';
import wowEmoji from '../assets/wow-emoji.png';
import sadEmoji from '../assets/sad-emoji.png';

export type ReactionType = 'like' | 'love' | 'care' | 'haha' | 'wow' | 'sad';

interface ReactionPickerProps {
  isVisible: boolean;
  onReactionSelect: (reaction: ReactionType) => void;
  currentReaction?: ReactionType | null;
  className?: string;
}

const reactionData = [
  { type: 'like' as ReactionType, label: 'Like', color: 'text-blue-500', customImage: likeEmoji },
  { type: 'love' as ReactionType, label: 'Love', color: 'text-red-500', customImage: loveEmoji },
  { type: 'care' as ReactionType, label: 'Care', color: 'text-yellow-500', customImage: careEmoji },
  { type: 'haha' as ReactionType, label: 'Haha', color: 'text-yellow-500', customImage: hahaEmoji },
  { type: 'wow' as ReactionType, label: 'Wow', color: 'text-orange-500', customImage: wowEmoji },
  { type: 'sad' as ReactionType, label: 'Sad', color: 'text-blue-400', customImage: sadEmoji }
];

const ReactionPicker = ({ isVisible, onReactionSelect, currentReaction, className }: ReactionPickerProps) => {
  const [hoveredReaction, setHoveredReaction] = useState<ReactionType | null>(null);

  if (!isVisible) return null;

  return (
    <TooltipProvider>
      <div 
        className={cn(
          "absolute bottom-8 left-0 bg-white dark:bg-gray-800 rounded-full shadow-xl border p-2 flex gap-1 transition-all duration-200 scale-100 opacity-100",
          "animate-scale-in z-50",
          className
        )}
      >
        {reactionData.map((reaction) => (
          <Tooltip key={reaction.type}>
            <TooltipTrigger asChild>
              <button
                onClick={() => onReactionSelect(reaction.type)}
                onMouseEnter={() => setHoveredReaction(reaction.type)}
                onMouseLeave={() => setHoveredReaction(null)}
                className={cn(
                  "relative w-12 h-12 rounded-full flex items-center justify-center transition-all duration-150",
                  "hover:scale-125 hover:bg-gray-100 dark:hover:bg-gray-700",
                  currentReaction === reaction.type && "scale-110 bg-gray-100 dark:bg-gray-700",
                  hoveredReaction === reaction.type && "scale-125"
                )}
              >
                <img 
                  src={reaction.customImage} 
                  alt={reaction.label}
                  className="w-8 h-8 object-contain"
                />
              </button>
            </TooltipTrigger>
            <TooltipContent>
              <p className="capitalize">{reaction.label}</p>
            </TooltipContent>
          </Tooltip>
        ))}
      </div>
    </TooltipProvider>
  );
};

export default ReactionPicker;